import React from 'react';
import { colors } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';
import { useMount } from '../../../../hooks';
import analytics from '../../../../modules/analytics';

import {
  BodyText,
  Tag,
  HeaderText,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayModal,
  Stack,
  Button,
  Absolute,
} from '../../../ui';

export const TEST_ID_PREFIX = 'ncd_cancellation_policy_acceptance_modal_';
export const TRACKING_PREFIX = 'NCD Cancellation Policy Modal ';

export interface INcdCancellationPolicyAcceptanceModalProps {
  providerId: number;
  onAccept: () => void;
}

const NcdCancellationPolicyAcceptanceModal:
React.FC<INcdCancellationPolicyAcceptanceModalProps> = ({
  providerId,
  onAccept,
}) => {
  useMount(() => analytics.track(`${TRACKING_PREFIX}Viewed`, { provider_id: providerId }));
  const handleAcceptButtonClick = () => {
    analytics.track(`${TRACKING_PREFIX}Accept Clicked`, { provider_id: providerId });
    onAccept();
  };
  return (
    <PortalOverlay
      name={`${TEST_ID_PREFIX}modal-overlay`}
    >
      <PortalOverlayModal
        position="fullScreen"
        color={colors.superLight}
      >
        <PortalOverlayHeader
          hideBottomBorder
          alignItems="flex-start"
          renderTitle={() => (
            <Box paddingTop={16}>
              <HeaderText level="3">StyleSeat rules &amp; restrictions</HeaderText>
            </Box>
          )}
        />
        <Stack space={8}>
          <Stack space={16} padding={24} color={colors.whiteWash}>
            <HeaderText level={2}>
              StyleSeat rules &amp; restrictions
            </HeaderText>
            <BodyText appearance="large">
              Same-day cancellations can ruin clients&apos;
              plans and reduce confidence in our community.
            </BodyText>
          </Stack>
          <Stack
            space={8}
            paddingLeft={24}
            paddingRight={48}
            paddingTop={24}
            paddingBottom={48}
            color={colors.whiteWash}
          >
            <HeaderText level={3}>What to know</HeaderText>
            <Inline>
              <Tag
                color={colors.slate}
                textTransform="none"
                borderRadius={16}
                fontWeight="400"
              >
                <Box
                  paddingBottom={3}
                >
                  <BodyText
                    appearance="small"
                    color={colors.whiteWash}
                  >
                    New Client from StyleSeat
                  </BodyText>
                </Box>
              </Tag>
            </Inline>
            <BodyText>
              The following restrictions only apply to appointments with this tag.
            </BodyText>
            <Inline maxWidth="100%" paddingTop={12} space={2}>
              <Stack minWidth={24} paddingTop={2}>
                <Box color={colors.sunshine} minHeight={101} minWidth={3} maxWidth={3}>
                  <Absolute top={12} left={-6}>
                    <Inline alignItems="center">
                      <Box
                        borderColor={colors.sunshine}
                        borderWidth={3}
                        borderRadius={16}
                        minWidth={16}
                        minHeight={16}
                        borderStyle="solid"
                        color={colors.whiteWash}
                      />
                      <Box color={colors.sunshine} minWidth={8} minHeight={1} />
                    </Inline>
                  </Absolute>
                </Box>
                <Box color={colors.red} minHeight={48} minWidth={3} maxWidth={3}>
                  <Absolute top={-6} left={-6}>
                    <Inline alignItems="center">
                      <Box
                        borderColor={colors.red}
                        borderWidth={3}
                        borderRadius={16}
                        minWidth={16}
                        minHeight={16}
                        borderStyle="solid"
                        color={colors.whiteWash}
                      />
                      <Box color={colors.red} minWidth={8} minHeight={1} />
                    </Inline>
                  </Absolute>
                </Box>
              </Stack>
              <Stack maxWidth={280} space={24} paddingTop={16}>
                <Stack>
                  <HeaderText level={4}>
                    Before the appointment
                  </HeaderText>
                  <BodyText>
                    You or the client can cancel the appointment.
                  </BodyText>
                </Stack>
                <Stack>
                  <HeaderText level={4}>
                    Day of the appointment
                  </HeaderText>
                  <BodyText>
                    Only the client can cancel the appointment.
                  </BodyText>
                </Stack>
              </Stack>
            </Inline>
          </Stack>
        </Stack>
        <Absolute bottom={24} minWidth="100%">
          <Box padding={24}>
            <Button
              appearance="primary-neue"
              onClick={handleAcceptButtonClick}
              testID={`${TEST_ID_PREFIX}accept_button`}
            >I Accept
            </Button>
          </Box>
        </Absolute>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};

export default NcdCancellationPolicyAcceptanceModal;
